/**
 * California Civil Claims Court Schema
 * Designed for the Evidencer ecosystem with Web3 integration
 */

// Default empty civil claims case
export const DEFAULT_SMALL_CLAIMS_CASE = {
  // Case Metadata
  caseId: "",
  caseNumber: "",
  filingDate: "",
  filingIPFSHash: "",
  currentStatusDate: "",
  currentStatus: "draft", // draft, filed, served, hearing_scheduled, hearing_completed, judgment_issued, appealed, closed
  statusHistory: [], // Array of status changes with dates
  
  // Filing Party Information
  plaintiff: {
    name: "",
    type: "individual", // individual, business, government
    address: {
      street: "",
      city: "",
      state: "CA",
      zipCode: ""
    },
    contact: {
      phone: "",
      email: ""
    },
    web3Identity: {
      walletAddress: "",
      ensName: "",
      verificationMethod: "", // signature, verifiable credential, etc.
      verificationTimestamp: "",
      verificationProof: ""
    }
  },
  
  // Defendant Information
  defendant: {
    name: "",
    type: "individual", // individual, business, government
    address: {
      street: "",
      city: "",
      state: "CA",
      zipCode: ""
    },
    contact: {
      phone: "",
      email: ""
    },
    web3Identity: {
      walletAddress: "",
      ensName: "",
      verificationMethod: "",
      verificationTimestamp: "",
      verificationProof: ""
    },
    serviceMethod: "", // personal, substituted, certified mail
    serviceDatetime: "",
    serviceProof: "", // IPFS hash to proof of service document
  },
  
  // Claim Details
  claim: {
    amount: 0, // Maximum $10,000 for individuals, $5,000 for businesses in CA
    type: "", // breach of contract, property damage, unpaid debt, etc.
    description: "",
    incidentDate: "",
    attachedContracts: [], // IPFS hashes to contract documents
    demandLetterSent: false,
    demandLetterDate: "",
    demandLetterIPFSHash: "",
    attemptedResolution: false,
    resolutionMethod: "", // mediation, direct communication, etc.
    resolutionDetails: ""
  },
  
  // Venue Information
  venue: {
    county: "",
    courthouse: "",
    department: "",
    address: {
      street: "",
      city: "",
      state: "CA",
      zipCode: ""
    },
    jurisdiction: "California Civil Claims",
    jurisdictionReason: "" // why this venue has jurisdiction
  },
  
  // Evidence
  evidence: {
    documents: [
      // {
      //   id: "",
      //   title: "",
      //   description: "",
      //   dateAdded: "",
      //   type: "", // contract, receipt, photo, communication, etc.
      //   ipfsHash: "", // IPFS CID
      //   encryptionStatus: "", // unencrypted, encrypted
      //   blockchainVerification: {
      //     txHash: "",
      //     timestamp: "",
      //     chainId: ""
      //   }
      // }
    ],
    witnesses: [
      // {
      //   name: "",
      //   contact: {
      //     email: "",
      //     phone: ""
      //   },
      //   relation: "",
      //   testimony: "",
      //   willAttend: false
      // }
    ]
  },
  
  // Hearing Information
  hearing: {
    scheduled: false,
    datetime: "",
    location: {
      courthouse: "",
      department: "",
      address: {
        street: "",
        city: "",
        state: "CA",
        zipCode: ""
      },
      remote: false,
      remoteDetails: ""
    },
    attendees: [],
    notes: "",
    postponementRequested: false,
    postponementReason: ""
  },
  
  // Judgment
  judgment: {
    infavorOf: "", // plaintiff or defendant
    issued: false,
    issueDate: "",
    amount: 0,
    interest: 0,
    interestRate: 0,
    paymentTerms: "",
    paymentDueDate: "",
    additionalTerms: "",
    satisfactionStatus: "", // unpaid, partially paid, fully paid
    amountPaid: 0,
    judgmentIPFSHash: "" // IPFS hash of judgment document
  },
  
  // Appeal Information
  appeal: {
    filed: false,
    filingDate: "",
    filedBy: "", // plaintiff or defendant
    reason: "",
    status: "", // pending, granted, denied
    hearingDate: "",
    appealIPFSHash: ""
  },
  
  // Blockchain & IPFS Integration
  web3Integration: {
    caseDigest: "", // Hash of core case data
    transactionHistory: [
      // {
      //   timestamp: "",
      //   txHash: "",
      //   chainId: "",
      //   action: "", // file, update, evidence, etc.
      //   description: ""
      // }
    ],
    ipfsHistory: [
      // {
      //   timestamp: "",
      //   cid: "",
      //   description: "",
      //   contentType: "" // filing, evidence, judgment, etc.
      // }
    ]
  },
  
  // Deadlines & Important Dates
  deadlines: {
    filingDeadline: "", // Statute of limitations
    serviceDeadline: "", // Within certain days of filing
    responseDeadline: "", // For defendant
    evidenceSubmissionDeadline: "",
    appealDeadline: "",
    satisfactionDeadline: ""
  },
  
  // Fee Information
  fees: {
    filingFee: 0,
    feeWaiverRequested: false,
    feeWaiverStatus: "", // pending, approved, denied
    additionalFees: [
      // { description: "", amount: 0 }
    ],
    totalPaid: 0,
    paymentMethod: "",
    paymentReference: ""
  }
};

// Case status options for dropdown
export const CASE_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "filed", label: "Filed with Court" },
  { value: "served", label: "Defendant Served" },
  { value: "hearing_scheduled", label: "Hearing Scheduled" },
  { value: "hearing_completed", label: "Hearing Completed" },
  { value: "judgment_issued", label: "Judgment Issued" },
  { value: "appealed", label: "Case Appealed" },
  { value: "closed", label: "Case Closed" }
];

// Claim types for dropdown
export const CLAIM_TYPES = [
  { value: "breach_of_contract", label: "Breach of Contract" },
  { value: "property_damage", label: "Property Damage" },
  { value: "personal_injury", label: "Personal Injury" },
  { value: "unpaid_debt", label: "Unpaid Debt" },
  { value: "return_of_property", label: "Return of Property/Security Deposit" },
  { value: "defective_product", label: "Defective Products/Services" },
  { value: "landlord_tenant", label: "Landlord/Tenant Dispute" },
  { value: "other", label: "Other Civil Matter" }
];

// California counties for venue selection
export const CA_COUNTIES = [
  { value: "alameda", label: "Alameda" },
  { value: "los_angeles", label: "Los Angeles" },
  { value: "orange", label: "Orange" },
  { value: "riverside", label: "Riverside" },
  { value: "sacramento", label: "Sacramento" },
  { value: "san_bernardino", label: "San Bernardino" },
  { value: "san_diego", label: "San Diego" },
  { value: "san_francisco", label: "San Francisco" },
  { value: "santa_clara", label: "Santa Clara" },
  // Add other CA counties as needed
];

// Evidence types for categorization
export const EVIDENCE_TYPES = [
  { value: "contract", label: "Contract or Agreement" },
  { value: "receipt", label: "Receipt or Invoice" },
  { value: "photo", label: "Photograph" },
  { value: "communication", label: "Communication (Email, Text, Letter)" },
  { value: "medical", label: "Medical Record" },
  { value: "expert", label: "Expert Report" },
  { value: "witness", label: "Witness Statement" },
  { value: "blockchain", label: "Blockchain Record" },
  { value: "other", label: "Other Evidence" }
];

// Chain IDs for blockchain integration
export const BLOCKCHAIN_NETWORKS = [
  { chainId: "1", name: "Ethereum Mainnet" },
  { chainId: "42161", name: "Arbitrum One" },
  { chainId: "137", name: "Polygon" },
  { chainId: "10", name: "Optimism" },
  { chainId: "8453", name: "Base" },
  { chainId: "5", name: "Goerli Testnet" }
];

// Helper function for creating a new case with defaults
export const createNewCase = () => {
  return {
    ...DEFAULT_SMALL_CLAIMS_CASE,
    caseId: `SC-${new Date().getTime()}`,
    filingDate: new Date().toISOString().split('T')[0]
  };
};

// Helper function for generating a status update
export const createStatusUpdate = (caseData, newStatus, notes = "") => {
  const statusUpdate = {
    status: newStatus,
    timestamp: new Date().toISOString(),
    notes: notes,
    updatedBy: caseData.plaintiff.name
  };
  
  // Create status history if it doesn't exist
  const statusHistory = caseData.statusHistory || [];
  
  return {
    ...caseData,
    currentStatus: newStatus,
    currentStatusDate: statusUpdate.timestamp,
    statusHistory: [...statusHistory, statusUpdate]
  };
};

// Helper function to add blockchain transaction record
export const addTransactionRecord = (caseData, txHash, chainId, action, description) => {
  const txRecord = {
    timestamp: new Date().toISOString(),
    txHash,
    chainId,
    action,
    description
  };
  
  return {
    ...caseData,
    web3Integration: {
      ...caseData.web3Integration,
      transactionHistory: [
        ...(caseData.web3Integration.transactionHistory || []),
        txRecord
      ]
    }
  };
};

// Helper function to add IPFS record
export const addIpfsRecord = (caseData, cid, description, contentType) => {
  const ipfsRecord = {
    timestamp: new Date().toISOString(),
    cid,
    description,
    contentType
  };
  
  return {
    ...caseData,
    web3Integration: {
      ...caseData.web3Integration,
      ipfsHistory: [
        ...(caseData.web3Integration.ipfsHistory || []),
        ipfsRecord
      ]
    }
  };
};

// Helper function for validating case data
export const validateCase = (caseData) => {
  const errors = {};
  
  // Plaintiff validation
  if (!caseData.plaintiff.name) errors.plaintiffName = "Plaintiff name is required";
  if (!caseData.plaintiff.address.street) errors.plaintiffAddress = "Plaintiff address is required";
  
  // Defendant validation
  if (!caseData.defendant.name) errors.defendantName = "Defendant name is required";
  if (!caseData.defendant.address.street) errors.defendantAddress = "Defendant address is required";
  
  // Claim validation
  if (!caseData.claim.amount) errors.claimAmount = "Claim amount is required";
  if (caseData.claim.amount > 10000 && caseData.plaintiff.type === "individual") {
    errors.claimAmountTooHigh = "Maximum claim for individuals is $10,000";
  }
  if (caseData.claim.amount > 5000 && caseData.plaintiff.type === "business") {
    errors.claimAmountTooHigh = "Maximum claim for businesses is $5,000";
  }
  if (!caseData.claim.description) errors.claimDescription = "Claim description is required";
  
  // Venue validation
  if (!caseData.venue.county) errors.venueCounty = "Venue county is required";
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

const smallClaimsSchema = {
  DEFAULT_SMALL_CLAIMS_CASE,
  CASE_STATUS_OPTIONS,
  CLAIM_TYPES,
  CA_COUNTIES,
  EVIDENCE_TYPES,
  BLOCKCHAIN_NETWORKS,
  createNewCase,
  createStatusUpdate,
  addTransactionRecord,
  addIpfsRecord,
  validateCase
};

export default smallClaimsSchema;